import React, { type ReactNode, useContext, useMemo, useReducer } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';

import { DialogContext } from '../dialog-context';
import { type Dialog, type DialogProviderReducerState } from '../types';

interface Action {
	type: 'close' | 'open';
	payload?: Pick<DialogProviderReducerState, 'DialogComponent'>;
}

type DialogProviderProps = {
	children: ReactNode;
};

const dialogProviderReducer = (state: DialogProviderReducerState, action: Action) => {
	switch (action.type) {
		case 'open':
			return {
				...state,
				DialogComponent: action.payload?.DialogComponent || null,
				isOpen: true,
			};
		case 'close':
			return {
				...state,
				DialogComponent: null,
				dialogProps: null,
				isOpen: false,
			};
		default:
			throw new Error(`Unhandled type for dialogReducer: ${action.type}`);
	}
};

const DialogWithModalTransition = (props: DialogProviderReducerState): React.JSX.Element | null => {
	const { DialogComponent, isOpen, onClose } = props;
	const { closeDialog } = useContext(DialogContext);

	if (!DialogComponent) {
		return null;
	}

	const handleCloseDialog = () => {
		closeDialog();

		if (onClose) {
			onClose();
		}
	};

	return (
		<ModalTransition>
			{isOpen && React.cloneElement(DialogComponent, { onClose: handleCloseDialog })}
		</ModalTransition>
	);
};

const DialogProvider = ({ children }: DialogProviderProps): React.JSX.Element => {
	const [dialogProviderState, dispatch] = useReducer(dialogProviderReducer, {
		DialogComponent: null,
		isOpen: false,
	});

	const value = useMemo(
		() => ({
			openDialog: (DialogComponent: Dialog | null) =>
				dispatch({
					type: 'open',
					payload: {
						DialogComponent,
					},
				}),
			closeDialog: () => dispatch({ type: 'close' }),
		}),
		[],
	);

	return (
		<DialogContext.Provider value={value}>
			{children}
			<DialogWithModalTransition {...dialogProviderState} />
		</DialogContext.Provider>
	);
};

export { DialogWithModalTransition, DialogProvider };
